/**
 * License Service
 * Handles fetching and managing policy approval license information
 */
import appConst from "../Utilities/appConst.json";
import en from "../locale/en.json";
import he from "../locale/he.json";

const CONNECT_URL = "https://connect.lupa.co.il";
const dictionaryByLanguage = { he, en };
/**
 * Creates a default license response when the API fails or returns invalid data
 * @param {string} currentLanguage - The current language ('he' or 'en')
 * @returns {Object} Default license response
 */
const createDefaultLicenseResponse = (currentLanguage) => {
  const dictionary =
    dictionaryByLanguage[currentLanguage] || dictionaryByLanguage.he;

  return {
    isValid: true,
    errorCode: 0,
    errorMessage: null,
    payload: {
      version: "1.2",
      language: currentLanguage,
      template: dictionary.policy_approval_default_template,
      placeholders: {
        terms: {
          label: dictionary.policy_approval_terms_label,
          url: "https://www.lupa.co.il/license/",
        },
        privacy: {
          label: dictionary.policy_approval_privacy_label,
          url: "https://www.lupa.co.il/privacy/",
        },
      },
    },
  };
};

/**
 * Fetches license information from the server
 * @param {string} currentLanguage - The current language ('he' or 'en')
 * @returns {Promise<Object>} License response with template and placeholders
 */
export const fetchLicense = async (currentLanguage = "he") => {
  try {
    const url = `${CONNECT_URL}/V2/api/entry.aspx?method=${appConst.LICENSE_API_METHOD}&lang=${currentLanguage}`;

    const response = await fetch(url);

    if (!response.ok) {
      console.warn(
        `License API returned ${response.status}, using default version`
      );
      return createDefaultLicenseResponse(currentLanguage);
    }

    const rawData = await response.json();

    if (!rawData.isValid) {
      console.error("License API returned invalid response, using default version");
      return createDefaultLicenseResponse(currentLanguage);
    }

    // Normalize server payLoad/paylaod to our internal payload field
    const normalizedResponse = {
      isValid: rawData.isValid,
      errorCode: rawData.errorCode,
      errorMessage: rawData.errorMessage ?? null,
      payload: rawData.payload ?? rawData.payLoad,
    };

    return normalizedResponse;
  } catch (error) {
    console.error(
      "Failed to fetch license from server, using default version:",
      error
    );
    return createDefaultLicenseResponse(currentLanguage);
  }
};

const handleLinkClick = (e, url) => {
  try {
    if (
      typeof window.LupaNative !== "undefined" &&
      window.LupaNative.openExternalLink
    ) {
      e.preventDefault();
      window.LupaNative.openExternalLink(url);
    }
  } catch (error) {
    console.error("Failed to open external link:", error);
    // Fallback to default browser behavior if native method fails
  }
};

/**
 * Parses template string and replaces placeholders with React links
 * @param {string} template - Template string with {placeholder} tokens
 * @param {Object} placeholders - Object containing placeholder data
 * @returns {Array<React.ReactNode>|string} Array of React elements and strings
 */
export const parseTemplate = (template, placeholders) => {
  if (!template) return null;
  if (!placeholders) return template;

  const parts = [];
  const placeholderPattern = /\{([^}]+)\}/g;
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = placeholderPattern.exec(template)) !== null) {
    const fullMatch = match[0];
    const placeholderKey = match[1];

    // Push text before the placeholder
    if (match.index > lastIndex) {
      parts.push(template.slice(lastIndex, match.index));
    }

    // Look up placeholder by key on the placeholders object
    const placeholderData = placeholders[placeholderKey];

    if (placeholderData?.label && placeholderData?.url) {
      parts.push(
        <a
          key={`${placeholderKey}-${keyIndex++}`}
          href={placeholderData.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-dark underline"
          onClick={(e) => handleLinkClick(e, placeholderData.url)}
        >
          {placeholderData.label}
        </a>
      );
    } else {
      // If we don't have data, keep the original token
      parts.push(fullMatch);
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Add any remaining text after the last placeholder
  if (lastIndex < template.length) {
    parts.push(template.slice(lastIndex));
  }
  return parts.length ? parts : template;
};
